import {
  ActivityIcon,
  BarChart3,
  LucideBanknote,
  LucideFileText,
  Mail,
  Plane,
  Warehouse,
  Sparkles,
} from 'lucide-react';
import { ISideNavItem } from '../types';
import {
  Cog8ToothIcon,
  ShoppingCartIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { SystemRoles } from '../enums/common';

const commonClassName =
  'size-4 flex place-items-center stroke-inherit text-inherit';
const showDocumentButtons =
  import.meta.env.VITE_SHOW_DOCUMENT_BUTTONS == 'true';
const aiAssistantEnabled = import.meta.env.VITE_AI_ASSISTANT_ENABLED === 'true';
const TRAVEL_NAVIGATION_ROLES = [
  SystemRoles.ADMIN,
  SystemRoles.TRAVEL_ADMIN,
  SystemRoles.TRAVEL_DESK,
  SystemRoles.TRAVEL_EMPLOYEE,
  SystemRoles.TRAVEL_FINANCE,
  SystemRoles.HR_ADMIN,
];

export const SIDENAV_MENU_ITEMS: ISideNavItem[] = [
  ...(aiAssistantEnabled
    ? [
      {
        title: 'AI Assistant',
        icon: <Sparkles className={commonClassName} />,
        path: '/ai-assistant',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.TRAVEL_ADMIN,
          SystemRoles.TRAVEL_DESK,
          SystemRoles.TRAVEL_EMPLOYEE,
          SystemRoles.TRAVEL_FINANCE,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_EMPLOYEE,
          SystemRoles.SALES_ORDER_FINANCE,
          SystemRoles.HR_ADMIN,
          SystemRoles.EMPLOYEE,
        ],
      } satisfies ISideNavItem,
    ]
    : []),
  // {
  //   title: 'Requisition',
  //   ...
  // },
  {
    title: 'Travel',
    icon: <Plane className={commonClassName} />,
    allowedRoles: TRAVEL_NAVIGATION_ROLES,
    subMenu: true,
    subMenuItems: [
      {
        title: 'Overview',
        path: '/travel/home',
        allowedRoles: TRAVEL_NAVIGATION_ROLES,
      },
      {
        title: 'Portfolio',
        path: '/travel/all',
        allowedRoles: TRAVEL_NAVIGATION_ROLES,
      },
      {
        title: 'My Approvals',
        path: '/travel/approvals',
        allowedRoles: TRAVEL_NAVIGATION_ROLES,
      },
      {
        title: 'Reports',
        path: '/travel/reports',
        allowedRoles: TRAVEL_NAVIGATION_ROLES,
      },
    ],
  },
  {
    title: 'Contracts',
    icon: <LucideFileText className={commonClassName} />,
    allowedRoles: [
      SystemRoles.ADMIN,
      SystemRoles.CONTRACT_ADMIN,
      SystemRoles.CONTRACT_EMPLOYEE,
      SystemRoles.CONTRACT_FINANCE,
    ],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Admin Dashboard',
        path: '/contracts/pnl-dashboard',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
        ],
      },
      {
        title: 'Overview',
        path: '/contracts/home',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
        ],
      },
      {
        title: 'Portfolio',
        path: '/contracts/all',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
        ],
      },
      {
        title: 'My Approvals',
        path: '/contracts/approvals',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
        ],
      },
      {
        title: 'Reports',
        path: '/contracts/reports',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
        ],
      },
      {
        title: 'Masters',
        path: '/contracts/masters',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.CONTRACT_ADMIN,
          SystemRoles.CONTRACT_EMPLOYEE,
          SystemRoles.CONTRACT_FINANCE,
          SystemRoles.EMPLOYEE,
        ],
      },
      ...(showDocumentButtons
        ? [
          {
            title: 'Document Templates',
            path: '/contracts/document-templates',
            allowedRoles: [
              SystemRoles.CONTRACT_ADMIN,
              SystemRoles.CONTRACT_EMPLOYEE,
              SystemRoles.CONTRACT_FINANCE,
            ],
          },
        ]
        : []),
    ],
  },
  {
    title: 'Payment Reminder',
    icon: <Mail className={commonClassName} />,
    allowedRoles: [SystemRoles.PAYMENT_EMPLOYEE, SystemRoles.PAYMENT_ADMIN],
    subMenu: true,
    subMenuItems: [
      {
        title: 'My List',
        path: '/payment-reminder/all',
        allowedRoles: [SystemRoles.PAYMENT_EMPLOYEE, SystemRoles.PAYMENT_ADMIN],
      },
    ],
  },
  {
    title: 'Sales Order',
    icon: <ShoppingCartIcon className={commonClassName} />,
    allowedRoles: [
      SystemRoles.ADMIN,
      SystemRoles.SALES_ORDER_ADMIN,
      SystemRoles.SALES_ORDER_EMPLOYEE,
      SystemRoles.SALES_ORDER_FINANCE,
    ],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Admin Dashboard',
        path: '/sales-order/new-dashboard',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_FINANCE,
          SystemRoles.SO_DASHBOARD_ADMIN,
        ],
      },
      {
        title: 'Home',
        path: '/sales-order/home',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_EMPLOYEE,
        ],
      },
      {
        title: 'My Orders',
        path: '/sales-order/all',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_EMPLOYEE,
        ],
      },
      {
        title: 'Approvals',
        path: '/sales-order/approvals',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_EMPLOYEE,
        ],
      },
      {
        title: 'Reports',
        path: '/sales-order/reports',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_EMPLOYEE,
          SystemRoles.SALES_ORDER_FINANCE,
        ],
      },
      {
        title: 'AI Report',
        path: '/sales-order/ai-report',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SALES_ORDER_FINANCE,
        ],
      },
      {
        title: 'Masters',
        path: '/sales-order/master-list',
        allowedRoles: [
          SystemRoles.ADMIN,
          SystemRoles.SALES_ORDER_ADMIN,
          SystemRoles.SO_MASTER_EDIT,
        ],
      },
    ],
  },
  {
    title: 'Activity Tracking',
    icon: <ActivityIcon className={commonClassName} />,
    allowedRoles: [SystemRoles.ACTIVITY_ADMIN, SystemRoles.ACTIVITY_EMPLOYEE],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Dashboard',
        path: '/activity/dashboard',
        allowedRoles: [
          SystemRoles.ACTIVITY_ADMIN,
          SystemRoles.ACTIVITY_EMPLOYEE,
        ],
      },
      {
        title: 'Home',
        path: '/activity/home',
        allowedRoles: [
          SystemRoles.ACTIVITY_ADMIN,
          SystemRoles.ACTIVITY_EMPLOYEE,
        ],
      },
      {
        title: 'Filters',
        path: '/activity/filter',
        allowedRoles: [SystemRoles.ACTIVITY_ADMIN],
      },
      {
        title: 'Assignment',
        path: '/activity/assignment',
        allowedRoles: [SystemRoles.ACTIVITY_ADMIN],
      },
      {
        title: 'Approvals',
        path: '/activity/approvals',
        allowedRoles: [
          SystemRoles.ACTIVITY_ADMIN,
          SystemRoles.ACTIVITY_EMPLOYEE,
        ],
      },
      {
        title: 'Reports',
        path: '/activity/reports',
        allowedRoles: [SystemRoles.ACTIVITY_ADMIN],
      },
    ],
  },
  {
    title: 'Advanced Payment',
    icon: <LucideBanknote className={commonClassName} />,
    allowedRoles: [
      SystemRoles.ADVANCED_PAYMENT_ADMIN,
      SystemRoles.ADVANCED_PAYMENT_EMPLOYEE,
    ],
    subMenu: true,
    subMenuItems: [
      {
        title: 'My Requests',
        path: '/advanced-payment/all',
        allowedRoles: [
          SystemRoles.ADVANCED_PAYMENT_ADMIN,
          SystemRoles.ADVANCED_PAYMENT_EMPLOYEE,
        ],
      },
      {
        title: 'Approvals',
        path: '/advanced-payment/approvals',
        allowedRoles: [
          SystemRoles.ADVANCED_PAYMENT_ADMIN,
          SystemRoles.ADVANCED_PAYMENT_EMPLOYEE,
        ],
      },
      {
        title: 'Reports',
        path: '/advanced-payment/reports',
        allowedRoles: [SystemRoles.ADVANCED_PAYMENT_ADMIN],
      },
    ],
  },
  {
    title: 'Warehouse',
    icon: <Warehouse className={commonClassName} />,
    allowedRoles: [SystemRoles.WAREHOUSE_ADMIN],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Structure',
        path: '/warehouse-structure',
        allowedRoles: [SystemRoles.WAREHOUSE_ADMIN],
      },
      {
        title: 'Operation',
        path: '/warehouse-operation',
        allowedRoles: [SystemRoles.WAREHOUSE_ADMIN],
      },
      {
        title: 'Inventory Report',
        path: '/warehouse-inventory-report',
        allowedRoles: [SystemRoles.WAREHOUSE_ADMIN],
      },
      {
        title: 'Item Mapping',
        path: '/settings/mappings/warehouse/item-mapping',
        allowedRoles: [SystemRoles.WAREHOUSE_ADMIN],
      },
    ],
  },
  {
    title: 'Purchase Order',
    icon: (
      <ShoppingCartIcon className="size-4 flex place-items-center stroke-[1.5px] text-inherit" />
    ),
    allowedRoles: [
      SystemRoles.PURCHASE_ORDER_ADMIN,
      SystemRoles.PURCHASE_ORDER_EMPLOYEE,
    ],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Home',
        path: '/purchase-order/home',
        allowedRoles: [
          SystemRoles.PURCHASE_ORDER_ADMIN,
          SystemRoles.PURCHASE_ORDER_EMPLOYEE,
        ],
      },
      {
        title: 'Registry',
        path: '/purchase-order/registry',
        allowedRoles: [
          SystemRoles.PURCHASE_ORDER_ADMIN,
          SystemRoles.PURCHASE_ORDER_EMPLOYEE,
        ],
      },
      {
        title: 'Approvals',
        path: '/purchase-order/approvals',
        allowedRoles: [
          SystemRoles.PURCHASE_ORDER_ADMIN,
          SystemRoles.PURCHASE_ORDER_EMPLOYEE,
        ],
      },
      {
        title: 'Reports',
        path: '/purchase-order/reports',
        allowedRoles: [
          SystemRoles.PURCHASE_ORDER_ADMIN,
          SystemRoles.PURCHASE_ORDER_EMPLOYEE,
        ],
      },
    ],
  },
  {
    title: 'Analytics',
    icon: <BarChart3 className={commonClassName} />,
    allowedRoles: [
      SystemRoles.ANALYTICS_ADMIN,
      SystemRoles.ANALYTICS_BUILDER,
      SystemRoles.ANALYTICS_VIEWER,
    ],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Dashboards',
        path: '/analytics/dashboards',
        allowedRoles: [
          SystemRoles.ANALYTICS_ADMIN,
          SystemRoles.ANALYTICS_BUILDER,
          SystemRoles.ANALYTICS_VIEWER,
        ],
      },
      {
        title: 'Charts',
        path: '/analytics/charts',
        allowedRoles: [
          SystemRoles.ANALYTICS_ADMIN,
          SystemRoles.ANALYTICS_BUILDER,
        ],
      },
      {
        title: 'Datasets',
        path: '/analytics/datasets',
        allowedRoles: [
          SystemRoles.ANALYTICS_ADMIN,
          SystemRoles.ANALYTICS_BUILDER,
        ],
      },
    ],
  },
  {
    title: 'User Access',
    icon: <UserIcon className={commonClassName} />,
    subMenu: true,
    allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
    subMenuItems: [
      {
        title: 'Users',
        path: '/uac/users',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
      {
        title: 'Roles',
        path: '/uac/roles',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
      {
        title: 'Permissions',
        path: '/uac/permissions',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
    ],
  },
  {
    title: 'Utility',
    icon: <WrenchScrewdriverIcon className={commonClassName} />,
    allowedRoles: [
      SystemRoles.ADMIN,
      SystemRoles.HR_ADMIN,
      SystemRoles.SALES_ORDER_ADMIN,
    ],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Uploader',
        path: '/utility/uploader',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
      {
        title: 'Execute Query',
        path: '/utility/execute-query',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
      {
        title: 'Send Notification',
        path: '/utility/notification',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.SALES_ORDER_ADMIN],
      },
    ],
  },
  {
    title: 'Settings',
    icon: <Cog8ToothIcon className={commonClassName} />,
    allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
    subMenu: true,
    subMenuItems: [
      {
        title: 'Business Mappings',
        path: '/settings/mappings',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
      {
        title: 'Form Field Mappings',
        path: '/settings/form-field-mappings',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
      {
        title: 'Approval Mappings',
        path: '/settings/approval-mappings',
        allowedRoles: [SystemRoles.ADMIN, SystemRoles.HR_ADMIN],
      },
    ],
  },
];
